import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CurrentUser } from '../../../common/models/user';
import { StorageService } from '../../../common/services/storage.service';
import { SearchService } from '../../../common/services/search.service';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { NotificationService } from '../../../common/services/notification.service';
import { GenericDialogComponent } from '../../../common/components/dialog/dialog';
import { Subject, takeUntil } from 'rxjs';
import { GetNotificationsCreatedByUserFilter, NotificationCreatedByUser } from '../../../common/models/notification';
import { FilterService } from '../../../common/components/filter/services/filter.service';
import { NotificationsFilterBuilder } from '../../../common/components/filter/builders/notifications-filter-builder';

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrl: './notifications.component.scss',
    imports: [MatCardModule,
        MatMenuModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatTooltipModule,
        MatIconModule,
        MatDialogModule,
        MatSnackBarModule,
        CommonModule,
    ]
})
export class NotificationsComponent implements OnInit {
    private destroy$ = new Subject<void>();

    notifications: NotificationCreatedByUser[] = [];
    filteredNotifications: NotificationCreatedByUser[] = [];
    user: CurrentUser | null = null;
  
    isToggled = false;
  
    constructor(
      private router: Router,
      private notificationService: NotificationService,
      private filterBuilder: NotificationsFilterBuilder,
      private storageService: StorageService,
      private searchService: SearchService,
      private filterService: FilterService,
      public themeService: CustomizerSettingsService,
      private dialog: MatDialog,
      private snackBar: MatSnackBar
    ) {}
  
    ngOnInit() {
      this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
        this.isToggled = isToggled;
      });
      this.searchService.currentSearchTerm$.pipe(takeUntil(this.destroy$)).subscribe(term => {
        this.applySearch(term);
      });
      this.filterService.filterChanges$.pipe(takeUntil(this.destroy$)).subscribe((filters) => {
        this.filterService.setFiltersInScope(filters);
        this.fetchNotifications();
      });
      this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
        this.user = user;
        this.initializeFilters();
      });
    }

     ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
     }

    initializeFilters(): void {
      const startYear = 2025;
      const currentYear = new Date().getFullYear();
    
      const config = this.filterBuilder
        .reset()
        .years(startYear)
        .build();

      this.filterService.setFilterConfig(config);
      this.filterService.restoreFiltersInScope({ year: currentYear });    
      
    }
  
    fetchNotifications(): void {
      const savedFilterValues = this.filterService.getFiltersInScope();
                    
      const filter = new GetNotificationsCreatedByUserFilter(
        this.user?.id,
        savedFilterValues?.year
      );

      this.notificationService.getNotificationsCreatedByUser(filter).pipe(takeUntil(this.destroy$)).subscribe({
        next: (data) => {
          this.notifications = data;
          this.filteredNotifications = data;
        },
        error: (error) => {
          console.error('Error fetching notifications', error);
        }
      });
    }

    applySearch(searchTerm: string): void {
      searchTerm = searchTerm.trim().toLowerCase();
      if (searchTerm) {
        this.filteredNotifications = this.notifications.filter((notification: NotificationCreatedByUser) => {
          return (
            notification.title.toLowerCase().includes(searchTerm) ||
            notification.message.toLowerCase().includes(searchTerm)
          );
        });
      } else {
        this.filteredNotifications = [...this.notifications];
      }
    }

    confirmDelete(notification: NotificationCreatedByUser): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite brisanje',
          message: `Jeste li sigurni da želite obrisati obavijest. Upozorenje, obavijest više neće biti vidljiva korisniku kojemu je poslana. "${notification.title}"?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.deleteNotification(notification.id);
        }
      });
    }
  
    deleteNotification(notificationId: string): void {
      this.notificationService.deleteNotification(notificationId).pipe(takeUntil(this.destroy$)).subscribe(
        () => {
          this.fetchNotifications();
          this.snackBar.open('Obavijest je obrisana', 'Zatvori', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top'
          });
        },
        (error) => {
          console.error('Error deleting grant', error);
        }
      );
    }

  
    goToCreateNotification(): void {
      this.router.navigate(['municipality', 'notifications', 'create']);
    }
}
